import type { ElementType, ReactElement, ReactNode } from 'react';

/**
 * Bedrock typography — the marketing site's type scale as components.
 *
 * The scale is deliberately SHORT (an eyebrow, two heading sizes, a lead, a
 * mono meta label) because the design uses exactly these five roles on every
 * surface. A page that needs a sixth is usually a page that should be reusing
 * one of the five.
 *
 * Every component here is tone-aware via the `onDark` prop rather than by
 * reading a context: a dark Band can contain a light card (the how-it-works
 * stage does exactly that), so tone is a property of the ELEMENT, not of its
 * ancestor. Making it explicit is what keeps that case correct.
 */

interface ToneProps {
  /** Render for a dark band ground rather than the light page. */
  readonly onDark?: boolean;
  readonly className?: string;
  readonly children: ReactNode;
}

/* ------------------------------------------------------------------ */
/* Eyebrow                                                             */
/* ------------------------------------------------------------------ */

export interface EyebrowProps extends ToneProps {
  /** Leading pulse dot — the design uses it for "live"/"status" eyebrows. */
  readonly dot?: boolean;
  readonly as?: ElementType;
}

/**
 * The small mono, letterspaced, uppercase label above a heading.
 *
 * Not `text-transform: uppercase` blindly: Arabic and Hebrew have no letter
 * case, so the transform is a no-op there while the wide `letter-spacing`
 * actively HARMS both scripts (it breaks Arabic's cursive joining). Both are
 * therefore dropped for RTL via the `rtl:` variants — the label still reads as
 * an eyebrow because of its size and colour.
 */
export function Eyebrow({
  dot = false,
  onDark = false,
  as: Tag = 'span',
  className,
  children,
}: EyebrowProps): ReactElement {
  return (
    <Tag
      className={[
        'inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em]',
        // `rtl:font-sans` alongside the case/tracking resets: neither Geist Mono
        // nor the `ui-monospace` fallback covers Arabic or Hebrew, so a mono
        // eyebrow in those locales resolves to a monospace face that renders
        // Arabic UNJOINED — every letter in its own fixed-width cell, which is
        // the same legibility damage letter-spacing does and is why the two
        // resets belong together.
        'rtl:font-sans rtl:normal-case rtl:tracking-normal',
        onDark ? 'text-[color:var(--bd-band-eyebrow)]' : 'text-[color:var(--bd-subtle)]',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {dot ? (
        <span
          aria-hidden="true"
          data-bd-anim
          className="size-1.5 flex-none rounded-full"
          style={{
            background: 'linear-gradient(120deg,var(--bd-cyan),var(--bd-violet))',
            animation: 'bdPulse 2.6s ease-in-out infinite',
          }}
        />
      ) : null}
      {children}
    </Tag>
  );
}

/* ------------------------------------------------------------------ */
/* Headings                                                            */
/* ------------------------------------------------------------------ */

export interface DisplayProps extends ToneProps {
  readonly as?: ElementType;
  readonly id?: string;
}

/**
 * The hero-scale headline. One per page, at most — it is the page's LCP text.
 *
 * `text-wrap: balance` is what keeps a two-line headline from leaving one
 * orphaned word, and it matters far more here than on body copy because the
 * six locales set this string at six different lengths.
 */
export function Display({
  as: Tag = 'h1',
  id,
  onDark = false,
  className,
  children,
}: DisplayProps): ReactElement {
  return (
    <Tag
      id={id}
      className={[
        'text-balance font-semibold leading-[0.96] tracking-[-0.045em]',
        'text-[clamp(2.5rem,6.1vw,5rem)]',
        onDark ? 'text-[color:var(--bd-band-ink)]' : 'text-[color:var(--bd-ink)]',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </Tag>
  );
}

/** The section-scale heading — the `<h2>` above a band's content. */
export function SectionTitle({
  as: Tag = 'h2',
  id,
  onDark = false,
  className,
  children,
}: DisplayProps): ReactElement {
  return (
    <Tag
      id={id}
      className={[
        'text-balance font-semibold leading-[1.04] tracking-[-0.038em]',
        'text-[clamp(1.75rem,3.6vw,2.75rem)]',
        onDark ? 'text-[color:var(--bd-band-ink)]' : 'text-[color:var(--bd-ink)]',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </Tag>
  );
}

/* ------------------------------------------------------------------ */
/* Body                                                                */
/* ------------------------------------------------------------------ */

export interface LeadProps extends ToneProps {
  /** `lg` is the hero's sub-headline; `md` is a section's. */
  readonly size?: 'md' | 'lg';
  readonly as?: ElementType;
}

/** The paragraph directly under a heading. */
export function Lead({
  size = 'md',
  as: Tag = 'p',
  onDark = false,
  className,
  children,
}: LeadProps): ReactElement {
  return (
    <Tag
      className={[
        'text-pretty leading-[1.62]',
        size === 'lg' ? 'text-[1.15rem]' : 'text-[1.03rem]',
        onDark ? 'text-[color:var(--bd-band-body)]' : 'text-[color:var(--bd-body)]',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </Tag>
  );
}

/**
 * A small mono meta label — counts, timings, statuses ("about 2 minutes",
 * "01 / 03"). Carries the AA text floor rather than the design's lighter
 * `#9a9aa2` (2.79:1); see `bedrock.css`.
 *
 * Drops its `letter-spacing` for RTL, for the reason `Eyebrow` documents above:
 * wide spacing breaks Arabic's cursive joining. It carried the spacing
 * unconditionally until an audit found it, which is why two call sites had
 * each patched it locally with `rtl:!tracking-normal` — a reset a primitive
 * needs at every call site belongs IN the primitive.
 */
export function MonoMeta({ onDark = false, className, children }: ToneProps): ReactElement {
  return (
    <span
      className={[
        'font-mono rtl:font-sans text-[11px] tracking-[0.1em] rtl:tracking-normal',
        onDark ? 'text-[color:var(--bd-band-dim)]' : 'text-[color:var(--bd-faint)]',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </span>
  );
}

export {
  GradientText,
  Accented,
  type GradientTextProps,
  type AccentedProps,
} from './gradient-text';
